import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { confirmPasswordReset, verifyPasswordResetCode } from "firebase/auth";
import { auth } from "../../firebase";
import { Key, ArrowLeft } from "lucide-react";

const SetNewPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const oobCode = searchParams.get("oobCode");
  const [verified, setVerified] = useState(true);
  const [email, setEmail] = useState("");

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!oobCode) {
      setError("Invalid or missing reset code.");
      return;
    }

    verifyPasswordResetCode(auth, oobCode)
      .then((email) => {
        setEmail(email);
        setVerified(true);
      })
      .catch(() => {
        setError("The password reset link is invalid or expired.");
      });
  }, [oobCode]);

  const handleReset = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");

    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[\\W_]).{8,}$/;

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    if (!passwordRegex.test(newPassword)) {
      setError(
        "Password must be at least 8 characters, include uppercase, lowercase, number, and symbol."
      );
      return;
    }

    try {
      await confirmPasswordReset(auth, oobCode!, newPassword);
      navigate("/reset-password-success");
    } catch (err: any) {
      if (err.code === "auth/weak-password") {
        setError("Password is too weak.");
      } else if (err.code === "auth/expired-action-code") {
        setError("The reset link has expired.");
      } else if (err.code === "auth/invalid-action-code") {
        setError("The reset link is invalid.");
      } else if (err.code === "auth/recent-login-required") {
        setError("You must log in again before changing password.");
      } else {
        setError("Failed to reset password. Try again.");
      }
    }
  };

  return (
    <div className="flex justify-center items-center h-screen ">
      <form onSubmit={handleReset} className="w-full max-w-md p-10">
        <div className="mb-6 text-center">
          <Key className="w-14 h-14 mx-auto text-blue-600 mb-2 rounded-full p-3 bg-gray-200" />
          <h2 className="text-4xl font-bold text-[#222]">Set New Password</h2>
          <p className="text-gray-600">
            Your new password must be different to previous used passwords
          </p>
        </div>

        {error && (
          <p className="text-red-500 mb-3 text-sm text-center">{error}</p>
        )}

        {!error && !verified && (
          <p className="text-gray-600 text-sm text-center">
            Verifying reset link...
          </p>
        )}

        {verified && (
          <div>
            <div className="flex flex-col gap-1 w-full mb-5">
              <label htmlFor="">Password</label>
              <input
                type="password"
                placeholder="New Password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full p-2 border border-gray-600 mb-4 rounded-md focus:outline-2 focus:border-0 outline-blue-600"
                required
              />
              <p className="text-xs text-gray-600 -mt-4">
                Must be contain at least 8 characters, 1 uppercase, 1 lowercase,
                1 number and 1 symbol
              </p>
            </div>

            <div className="flex flex-col gap-1 w-full mb-5">
              <label htmlFor="">Confirm Password</label>
              <input
                type="password"
                placeholder="Confirm Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full p-2 border border-gray-600 mb-4 rounded-md focus:outline-2 focus:border-0 outline-blue-600"
                required
              />
            </div>

            <button
              type="submit"
              className="w-full bg-blue-600 text-white py-2 rounded-md hover:bg-blue-700 cursor-pointer"
            >
              Set New Password
            </button>
          </div>
        )}

        <button
          type="button"
          onClick={() => navigate("/")}
          className="flex items-center justify-center gap-1 mt-4 text-sm text-gray-600 hover:underline mx-auto cursor-pointer"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Login
        </button>
      </form>
    </div>
  );
};

export default SetNewPassword;
